import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  FileText, Plus, Search, Download, ClipboardList, AlertTriangle, 
  CheckCircle, Calendar, User, Truck, MoreHorizontal, Pencil, Trash2,
  FileCheck, Clock, Eye
} from 'lucide-react';

const Forms = () => {
  const { api, user } = useAuth();
  const { hasFeature, hasTierFeature, currentTier } = useTier();
  const isAdmin = user?.role === 'admin';
  const hasCustomForms = hasTierFeature('custom_forms') || currentTier === 'advanced' || currentTier === 'professional';
  
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('submissions');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    form_type: 'job_linked',
    fields: [],
    is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, submissionsRes] = await Promise.all([
        api.get('/form-templates'),
        api.get('/form-submissions')
      ]);
      setTemplates(templatesRes.data);
      setSubmissions(submissionsRes.data);
    } catch (error) {
      toast.error('Failed to fetch forms data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewSubmission = async (submission) => {
    try {
      const res = await api.get(`/form-submissions/${submission.id}`);
      setSelectedSubmission(res.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load submission details');
    }
  };

  const handleOpenTemplateDialog = (template = null) => {
    if (template) {
      setSelectedTemplate(template);
      setTemplateForm({
        name: template.name,
        description: template.description || '',
        form_type: template.form_type,
        fields: template.fields || [],
        is_active: template.is_active
      });
    } else {
      setSelectedTemplate(null);
      setTemplateForm({
        name: '',
        description: '',
        form_type: 'job_linked',
        fields: [],
        is_active: true
      });
    }
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (selectedTemplate && !selectedTemplate.is_system) {
        await api.put(`/form-templates/${selectedTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/form-templates', templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/form-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const addField = () => {
    setTemplateForm({
      ...templateForm,
      fields: [
        ...templateForm.fields,
        {
          id: `field_${Date.now()}`,
          type: 'text',
          label: '',
          required: false,
          options: [],
          placeholder: ''
        }
      ]
    });
  };

  const updateField = (index, updates) => {
    const newFields = [...templateForm.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setTemplateForm({ ...templateForm, fields: newFields });
  };

  const removeField = (index) => {
    setTemplateForm({
      ...templateForm,
      fields: templateForm.fields.filter((_, i) => i !== index)
    });
  };

  const getFieldTypeLabel = (type) => {
    const types = {
      text: 'Text',
      number: 'Number',
      textarea: 'Long Text',
      checkbox: 'Checkbox',
      select: 'Dropdown',
      date: 'Date',
      yes_no: 'Yes/No',
      signature: 'Signature',
      photo: 'Photo'
    };
    return types[type] || type;
  };

  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return <span className="text-muted-foreground">-</span>;
    }
    
    switch (field?.type) {
      case 'yes_no':
        return value === true || value === 'yes' || value === 'Yes' ? (
          <Badge variant="outline" className="bg-green-500/20 text-green-500">Yes</Badge>
        ) : (
          <Badge variant="outline" className="bg-red-500/20 text-red-500">No</Badge>
        );
      case 'checkbox':
        return value ? <CheckCircle className="h-4 w-4 text-green-500" /> : <span>-</span>;
      case 'photo':
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
            View Photo
          </a>
        ) : '-';
      case 'signature':
        return value ? (
          <img src={value} alt="Signature" className="h-12 border rounded" />
        ) : '-';
      default:
        return <span>{String(value)}</span>;
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = 
      sub.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.operator_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.equipment_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterType === 'all') return matchesSearch;
    
    const template = templates.find(t => t.id === sub.template_id);
    return matchesSearch && template?.form_type === filterType;
  });

  // For Basic tier, only show system templates
  const displayedTemplates = hasCustomForms ? templates : templates.filter(t => t.is_system);

  const filteredTemplates = displayedTemplates.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[#ffd108]" />
            Forms
          </h1>
          <p className="text-sm text-muted-foreground">
            {hasCustomForms 
              ? 'View submitted forms and manage templates'
              : 'View submitted forms and use system templates'
            }
          </p>
        </div>
      </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{submissions.length}</p>
                  <p className="text-xs text-muted-foreground">Total Submissions</p>
                </div>
              </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <FileCheck className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{submissions.filter(s => templates.find(t => t.id === s.template_id)?.form_type === 'job_linked').length}</p>
                <p className="text-xs text-muted-foreground">Pre-Start Checks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{submissions.filter(s => s.template_name?.includes('Incident')).length}</p>
                <p className="text-xs text-muted-foreground">Incident Reports</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <ClipboardList className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{displayedTemplates.length}</p>
                <p className="text-xs text-muted-foreground">Form Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
            {activeTab === 'submissions' && (
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="job_linked">Job-Linked</SelectItem>
                  <SelectItem value="standalone">Standalone</SelectItem>
                </SelectContent>
              </Select>
            )}
            {activeTab === 'templates' && isAdmin && hasCustomForms && (
              <Button onClick={() => handleOpenTemplateDialog()} className="btn-volt">
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            )}
          </div>
        </div>

        {/* Submissions Tab */}
        <TabsContent value="submissions" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Form</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>Asset/Job</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubmissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No form submissions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSubmissions.map((sub) => {
                      const template = templates.find(t => t.id === sub.template_id);
                      return (
                        <TableRow key={sub.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{sub.template_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {sub.operator_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            {sub.equipment_name ? (
                              <div className="flex items-center gap-2">
                                <Truck className="h-4 w-4 text-muted-foreground" />
                                {sub.equipment_name}
                              </div>
                            ) : sub.job_description ? (
                              <span className="text-sm text-muted-foreground">{sub.job_description}</span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              {formatDate(sub.submitted_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              template?.form_type === 'standalone' 
                                ? 'bg-amber-500/20 text-amber-500' 
                                : 'bg-blue-500/20 text-blue-500'
                            }>
                              {template?.form_type === 'standalone' ? 'Standalone' : 'Job-Linked'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewSubmission(sub)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          {/* Upgrade prompt for Basic tier */}
          {!hasCustomForms && (
            <UpgradePrompt 
              feature="custom_forms"
              requiredTier="advanced"
              title="Custom Forms Builder"
              description="Create your own custom forms tailored to your business needs. Basic tier includes system forms for Pre-Start checks and Incident Reports."
              className="mb-4"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="relative">
                {template.is_system && (
                  <Badge className="absolute top-2 right-2 bg-[#ffd108] text-black">
                    System
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {template.name}
                  </CardTitle>
                  <CardDescription>{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Type:</span>
                      <Badge variant="outline">
                        {template.form_type === 'standalone' ? 'Standalone' : 'Job-Linked'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Fields:</span>
                      <span>{template.fields?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Status:</span>
                      <Badge variant={template.is_active ? 'default' : 'secondary'}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t flex justify-end gap-2">
                    {/* Edit button - available for all templates */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenTemplateDialog(template)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    {/* Delete button - only for non-system templates and Advanced+ tiers */}
                    {isAdmin && !template.is_system && hasCustomForms && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* View Submission Sheet (Flyout Panel) */}
      <Sheet open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">{selectedSubmission?.template_name}</SheetTitle>
          </SheetHeader>
          {selectedSubmission && (
            <div className="space-y-4 py-4">
              {/* Submission Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Operator</p>
                    <p className="font-medium">{selectedSubmission.operator_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="font-medium">{formatDate(selectedSubmission.submitted_at)}</p>
                  </div>
                  {selectedSubmission.equipment_name && (
                    <div>
                      <p className="text-xs text-muted-foreground">Asset</p>
                      <p className="font-medium">{selectedSubmission.equipment_name}</p>
                    </div>
                  )}
                  {selectedSubmission.job_description && (
                    <div>
                      <p className="text-xs text-muted-foreground">Job</p>
                      <p className="font-medium">{selectedSubmission.job_description}</p>
                    </div>
                  )}
                </div>

                {/* Form Responses */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Responses
                  </h4>
                  {selectedSubmission.template?.fields?.map((field) => (
                    <div key={field.id} className="flex justify-between items-start py-2 border-b">
                      <span className="text-sm font-medium">{field.label}</span>
                      <span className="text-sm text-right">
                        {renderFieldValue(field, selectedSubmission.data?.[field.id])}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Signature */}
                {selectedSubmission.signature_data && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Signature
                    </h4>
                    <img 
                      src={selectedSubmission.signature_data} 
                      alt="Signature" 
                      className="border rounded-lg max-w-xs"
                    />
                  </div>
                )}

                {/* Photos */}
                {selectedSubmission.photos?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Photos
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedSubmission.photos.map((photo, idx) => (
                        <a key={idx} href={photo} target="_blank" rel="noopener noreferrer">
                          <img src={photo} alt={`Photo ${idx + 1}`} className="rounded-lg border hover:opacity-80" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
          <SheetFooter className="pt-4 border-t mt-6">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button className="btn-volt">
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Template Editor Sheet (Flyout Panel) */}
      <Sheet open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {selectedTemplate ? 'Edit Template' : 'Create Template'}
            </SheetTitle>
            {selectedTemplate?.is_system && (
              <p className="text-sm text-muted-foreground">System template - you can edit fields but cannot rename</p>
            )}
          </SheetHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Template Name</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                      placeholder="e.g., Daily Inspection"
                      required
                      disabled={selectedTemplate?.is_system}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Form Type</Label>
                    <Select
                      value={templateForm.form_type}
                      onValueChange={(v) => setTemplateForm({ ...templateForm, form_type: v })}
                      disabled={selectedTemplate?.is_system}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="job_linked">Job-Linked</SelectItem>
                        <SelectItem value="standalone">Standalone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                    placeholder="Brief description of this form..."
                    disabled={selectedTemplate?.is_system}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Form Fields</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addField}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Field
                    </Button>
                  </div>

                  {templateForm.fields.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <p className="text-muted-foreground">No fields added yet</p>
                      <p className="text-sm text-muted-foreground">Click "Add Field" to start building your form</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {templateForm.fields.map((field, index) => (
                        <div key={field.id} className="p-3 border rounded-lg space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Field {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeField(index)}
                              className="text-destructive h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Label</Label>
                              <Input
                                value={field.label}
                                onChange={(e) => updateField(index, { label: e.target.value })}
                                placeholder="Field label"
                                required
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Type</Label>
                              <Select
                                value={field.type}
                                onValueChange={(v) => updateField(index, { type: v })}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="number">Number</SelectItem>
                                  <SelectItem value="textarea">Long Text</SelectItem>
                                  <SelectItem value="yes_no">Yes/No</SelectItem>
                                  <SelectItem value="checkbox">Checkbox</SelectItem>
                                  <SelectItem value="select">Dropdown</SelectItem>
                                  <SelectItem value="date">Date</SelectItem>
                                  <SelectItem value="signature">Signature</SelectItem>
                                  <SelectItem value="photo">Photo</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {field.type === 'select' && (
                            <div className="space-y-1">
                              <Label className="text-xs">Options (comma separated)</Label>
                              <Input
                                value={field.options?.join(', ') || ''}
                                onChange={(e) => updateField(index, { 
                                  options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                                })}
                                placeholder="Option 1, Option 2, Option 3"
                              />
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={field.required}
                              onCheckedChange={(v) => updateField(index, { required: v })}
                            />
                            <Label className="text-xs">Required field</Label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {selectedTemplate ? 'Update Template' : 'Create Template'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      </div>
  );
};

export default Forms;
